import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { FileContext, FileContextProvider } from "@/components/FileUploadAsync";
import { useOrderItems, useToasts } from "@/hooks";

import { ModalAddDeliveryLabelDropzone } from "./_partials";
import {
  type InferredModalAddDeliveryLabelSchema,
  modalAddDeliveryLabelSchema,
} from "./ModalAddDeliveryLabel.schema";
import { type ModalAddDeliveryLabelProps } from "./ModalAddDeliveryLabel.types";

export const ModalAddDeliveryLabel = ({
  isOpen,
  onOpenChange,
  orderId,
  orderItemId,
  addToast,
}: ModalAddDeliveryLabelProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  const [isLoading, setIsLoading] = useState(false);

  const preferredAddToast = addToast || add;

  const { useAddOrderItemDeliveryLabel } = useOrderItems();
  const { mutateAsync: addOrderItemDeliveryLabel } =
    useAddOrderItemDeliveryLabel();

  const methods = useForm<InferredModalAddDeliveryLabelSchema>({
    resolver: zodResolver(modalAddDeliveryLabelSchema),
    defaultValues: {
      fileSignedId: undefined,
    },
  });

  const onSubmit = async (data: InferredModalAddDeliveryLabelSchema) => {
    if (!data.fileSignedId) {
      return;
    }

    try {
      await addOrderItemDeliveryLabel({
        orderId,
        orderItemId,
        orderItemAddDeliveryLabelCommand: {
          deliveryLabelFileSignedId: data.fileSignedId,
        },
      });

      preferredAddToast({
        state: "success",
        title: t("modals.addDeliveryLabel.toast.success"),
      });

      onOpenChange(false);
    } catch (error) {
      console.error(error);
      preferredAddToast({
        state: "error",
        title: t("toast.generic.error"),
      });
    }
  };

  const onError = () => {
    preferredAddToast({
      state: "error",
      title: t("toast.generic.error"),
    });
  };

  return (
    <FileContextProvider>
      <FileContext.Consumer>
        {(ctx) => (
          <KdsModal
            isOpen={isOpen}
            size="sm"
            onOpenChange={onOpenChange}
            dialogProps={{
              dialogHeaderProps: {
                title: t("modals.addDeliveryLabel.title"),
                description: t("modals.addDeliveryLabel.description"),
              },
              content: (
                <FormProvider {...methods}>
                  <ModalAddDeliveryLabelDropzone
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                </FormProvider>
              ),
              dialogFooterProps: {
                buttonProps: [
                  {
                    children: t("modals.addDeliveryLabel.cancelButton"),
                    color: "neutral",
                    variant: "ghost",
                    isLoading: methods.formState.isSubmitting,
                    disabled: ctx.isUploading || methods.formState.isSubmitting,
                    onPress: () => onOpenChange(false),
                  },
                  {
                    children: t("modals.addDeliveryLabel.validateButton"),
                    type: "submit",
                    isLoading: methods.formState.isSubmitting,
                    disabled: ctx.isUploading || methods.formState.isSubmitting,
                    onPress: () =>
                      void methods.handleSubmit(onSubmit, onError)(),
                  },
                ],
              },
            }}
          />
        )}
      </FileContext.Consumer>
    </FileContextProvider>
  );
};
